import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Globe, Search, Sparkles, ShieldAlert, AlertTriangle, Flag,
  FileText, Loader2, CheckCircle, XCircle, TrendingUp, RefreshCw, Clock
} from "lucide-react";
import { toast } from "sonner";
import NotFoundCard from "../components/NotFoundCard";
import IdentityResultCard from "../components/IdentityResultCard";
import { fromSanctions } from "../lib/identityNormalizers";

const API = `${window.location.origin}/api/opensanctions`;

const FLAG_COLORS = {
  critical: { bg: "rgba(220, 38, 38, 0.18)", border: "#dc2626", text: "#fca5a5" },
  high:     { bg: "rgba(239, 68, 68, 0.12)", border: "#ef4444", text: "#ef4444" },
  medium:   { bg: "rgba(245, 158, 11, 0.10)", border: "#f59e0b", text: "#f59e0b" },
  low:      { bg: "rgba(34, 197, 94, 0.10)",  border: "#22c55e", text: "#22c55e" },
};

// Progress bar removed per Feb-2026 redesign — screening now returns in
// <1.5s via the pre-indexed sanctions_index, so a simple in-flight spinner
// + post-result `elapsed_ms` chip replaces the step-by-step bar.

function StatCard({ label, value, color, icon: Icon }) {
  return (
    <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "10px", padding: "18px" }}>
      <div className="flex items-center justify-between mb-2">
        <div style={{ color: "#64748b", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1.5px" }}>{label}</div>
        <Icon style={{ width: 16, height: 16, color }} />
      </div>
      <div style={{ color, fontSize: "28px", fontWeight: 700 }}>{value}</div>
    </div>
  );
}

function IndiaFlagsRow({ flags }) {
  const items = [
    { key: "sebi_debarred", label: "SEBI Debarred" },
    { key: "nse_debarred", label: "NSE Debarred" },
    { key: "uapa_listed", label: "UAPA Listed" },
    { key: "rbi_defaulter", label: "RBI Defaulter" },
  ];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "8px", marginBottom: "16px" }}>
      {items.map((it) => {
        const hit = !!flags?.[it.key];
        return (
          <div key={it.key} style={{
            background: hit ? "rgba(239, 68, 68, 0.12)" : "rgba(34, 197, 94, 0.08)",
            border: hit ? "1px solid #ef4444" : "1px solid #1e2530",
            borderRadius: "8px", padding: "10px", textAlign: "center",
          }}>
            <div className="flex items-center justify-center gap-1 mb-1">
              {hit ? <XCircle style={{ width: 12, height: 12, color: "#ef4444" }} /> : <CheckCircle style={{ width: 12, height: 12, color: "#22c55e" }} />}
              <span style={{ color: hit ? "#ef4444" : "#22c55e", fontSize: "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px" }}>
                {hit ? "HIT" : "CLEAR"}
              </span>
            </div>
            <div style={{ color: "#cbd5e1", fontSize: "11px", fontWeight: 600 }}>{it.label}</div>
          </div>
        );
      })}
    </div>
  );
}

function MatchCard({ m }) {
  return (
    <div style={{ background: "#080c12", border: "1px solid #ef4444", borderRadius: "8px", padding: "12px 14px", marginBottom: "8px" }}>
      <div className="flex items-center justify-between mb-2">
        <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 700 }}>{m.name}</div>
        <div className="flex items-center gap-2">
          <span style={{ background: "rgba(239, 68, 68, 0.18)", color: "#ef4444", fontSize: "10px", padding: "2px 8px", borderRadius: "4px", fontWeight: 700 }}>Score {m.score}</span>
          <span style={{ background: "#1e2530", color: "#94a3b8", fontSize: "10px", padding: "2px 8px", borderRadius: "4px", fontFamily: "ui-monospace, monospace" }}>{m.id}</span>
        </div>
      </div>
      {m.reason && <div style={{ color: "#cbd5e1", fontSize: "11px", marginBottom: "6px" }}>{m.reason}</div>}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "6px" }}>
        {(m.lists_matched || []).map((l, idx) => (
          <span key={idx} style={{ background: "rgba(239, 68, 68, 0.10)", color: "#fca5a5", fontSize: "10px", padding: "2px 8px", borderRadius: "4px", fontWeight: 600 }}>{l}</span>
        ))}
      </div>
      <div style={{ color: "#64748b", fontSize: "10px", display: "flex", gap: "12px" }}>
        <span>Country: <strong style={{ color: "#94a3b8" }}>{m.country || "—"}</strong></span>
        {m.nationality && <span>Nationality: <strong style={{ color: "#94a3b8" }}>{m.nationality}</strong></span>}
        {m.sanctioned_on && <span>Listed: <strong style={{ color: "#94a3b8" }}>{m.sanctioned_on}</strong></span>}
      </div>
    </div>
  );
}

export function OpenSanctionsPanel({ initialName = null, compact = false }) {
  const [queryName, setQueryName] = useState(initialName || "");
  const [looking, setLooking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [risk, setRisk] = useState(null);
  const [history, setHistory] = useState([]);
  const [stats, setStats] = useState({ total: 0, sanctioned: 0, uapa: 0, cases_created: 0 });
  const [startedAt, setStartedAt] = useState(null);
  const [screeningFor, setScreeningFor] = useState("");
  const [lastError, setLastError] = useState(null); // {kind: 'timeout'|'gateway', query, message}
  const inFlightRef = useRef(null);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/lookups`, { withCredentials: true });
      setHistory(data.lookups || []);
      setStats(data.stats || { total: 0, sanctioned: 0, uapa: 0, cases_created: 0 });
    } catch (err) { console.error("[OpenSanctions] Failed to load history:", err); }
  }, []);

  useEffect(() => { if (!compact) loadHistory(); }, [loadHistory, compact]);

  const runLookup = async () => {
    if (!queryName.trim()) { toast.error("Enter a name to screen"); return; }
    const submitName = queryName.trim();
    const requestId = Symbol("sanctions-lookup");
    inFlightRef.current = requestId;
    setLooking(true);
    setResult(null);
    setRisk(null);
    setLastError(null);
    setScreeningFor(submitName);
    setStartedAt(Date.now());
    try {
      const { data } = await axios.post(
        `${API}/lookup`,
        { query_name: submitName },
        {
          withCredentials: true,
          timeout: 90000, // 90s — covers cold Claude (43s p99) + ingress overhead
          headers: { "Content-Type": "application/json" },
        }
      );
      // Drop stale responses if a newer search was kicked off in the meantime.
      if (inFlightRef.current !== requestId) return;
      setResult(data.result);
      setRisk(data.risk);
      const matches = data.result?.total_matches || 0;
      toast.success(matches > 0 ? `${matches} match(es) — ${data.risk?.severity_level?.toUpperCase()}` : "No sanctions hits — CLEAR");
    } catch (e) {
      if (inFlightRef.current !== requestId) return;
      const isTimeout = e?.code === "ECONNABORTED" || /timeout/i.test(e?.message || "");
      const isGateway = [502, 503, 504].includes(e?.response?.status);
      if (isTimeout || isGateway) {
        setLastError({
          kind: isTimeout ? "timeout" : "gateway",
          query: submitName,
          message: isTimeout
            ? "Search taking longer than expected. Claude AI is busy — please try again."
            : `Gateway error (HTTP ${e?.response?.status || "?"}) — please try again.`,
        });
        toast.error("Sanctions search timed out — retry available below.");
      } else {
        setLastError(null);
        toast.error(e.response?.data?.detail || "Lookup failed");
      }
    } finally {
      if (inFlightRef.current === requestId) {
        setLooking(false);
        setStartedAt(null);
      }
    }
  };

  const retryLookup = () => {
    if (!lastError) return;
    setQueryName(lastError.query);
    setLastError(null);
    // Defer the call so React renders the cleared error before refiring.
    setTimeout(() => runLookup(), 0);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/save`,
        { query_name: queryName, result, link_to_customer_id: null },
        { withCredentials: true }
      );
      if (data.created_case) {
        toast.success(`Saved. Case ${data.created_case.case_id} auto-created.`);
      } else {
        toast.success("Screening saved.");
      }
      setQueryName(""); setResult(null); setRisk(null);
      if (!compact) loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="opensanctions-panel">
      {!compact && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "16px", marginBottom: "24px" }}>
          <StatCard label="Total Screenings" value={stats.total} color="#2563eb" icon={Globe} />
          <StatCard label="Sanctioned Hits" value={stats.sanctioned} color="#ef4444" icon={ShieldAlert} />
          <StatCard label="UAPA Listed" value={stats.uapa} color="#fca5a5" icon={Flag} />
          <StatCard label="Cases Created" value={stats.cases_created} color="#f59e0b" icon={FileText} />
        </div>
      )}

      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: compact ? "16px" : "24px" }}>
        <div className="flex items-center gap-2 mb-4">
          <Sparkles style={{ width: 18, height: 18, color: "#2563eb" }} />
          <h2 style={{ fontSize: compact ? "14px" : "16px", fontWeight: 700, margin: 0, color: "#f1f5f9" }}>
            {compact ? "Sanctions Quick Screen" : "Global + India Sanctions Screening"}
          </h2>
        </div>

        <div className="flex gap-3">
          <input
            type="text"
            value={queryName}
            onChange={(e) => setQueryName(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && runLookup()}
            placeholder="Person or company name (e.g., 'Mehul Choksi', 'Hawala Network')"
            data-testid="opensanctions-query-input"
            style={{ flex: 1, background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "10px 14px", color: "#f1f5f9", fontSize: "13px" }}
          />
          <button
            onClick={runLookup}
            disabled={looking}
            data-testid="opensanctions-lookup-btn"
            style={{
              background: looking ? "#1e2530" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
              border: "none", borderRadius: "8px", color: "white", padding: "10px 22px",
              fontWeight: 600, fontSize: "13px", cursor: looking ? "wait" : "pointer",
              display: "flex", alignItems: "center", gap: "8px", whiteSpace: "nowrap",
            }}
          >
            {looking ? (<><Loader2 className="animate-spin" style={{ width: 14, height: 14 }} /> Screening...</>) : (<><Search style={{ width: 14, height: 14 }} /> Screen Sanctions</>)}
          </button>
        </div>
        <div style={{ color: "#64748b", fontSize: "11px", marginTop: "8px" }}>
          OFAC · UN · EU · SEBI · NSE · RBI · MHA UAPA · 200+ datasets · Powered by Claude Sonnet 4.5
        </div>
      </div>

      {/* Timeout / gateway error banner with retry */}
      {lastError && !looking && (
        <div
          data-testid="sanctions-timeout-banner"
          style={{
            background: "rgba(245, 158, 11, 0.08)",
            border: "1px solid #f59e0b",
            borderRadius: "12px",
            padding: "18px 20px",
            marginTop: "20px",
            display: "flex",
            alignItems: "center",
            gap: "14px",
          }}
        >
          <Clock style={{ width: 22, height: 22, color: "#f59e0b", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <div style={{ color: "#fbbf24", fontSize: "13px", fontWeight: 700, marginBottom: "2px" }}>
              {lastError.kind === "timeout" ? "Request Timed Out" : "Gateway Error"}
            </div>
            <div style={{ color: "#cbd5e1", fontSize: "12px" }}>{lastError.message}</div>
            <div style={{ color: "#94a3b8", fontSize: "11px", marginTop: "4px" }}>
              Query: <strong style={{ color: "#f1f5f9" }}>"{lastError.query}"</strong>
            </div>
          </div>
          <button
            onClick={retryLookup}
            data-testid="sanctions-retry-btn"
            style={{
              background: "linear-gradient(135deg, #f59e0b, #d97706)",
              border: "none",
              borderRadius: "6px",
              color: "white",
              padding: "10px 18px",
              fontWeight: 700,
              fontSize: "12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              whiteSpace: "nowrap",
            }}
          >
            <RefreshCw style={{ width: 13, height: 13 }} /> Retry
          </button>
          <button
            onClick={() => setLastError(null)}
            data-testid="sanctions-dismiss-error-btn"
            style={{
              background: "transparent",
              border: "1px solid #1e2530",
              borderRadius: "6px",
              color: "#94a3b8",
              padding: "10px 14px",
              fontWeight: 600,
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* In-flight indicator — minimal */}
      {looking && startedAt && (
        <div
          data-testid="screening-inflight"
          style={{
            background: "#0d1117",
            border: "1px solid #2563eb55",
            borderRadius: "10px",
            padding: "14px 18px",
            marginTop: "16px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
            color: "#cbd5e1",
            fontSize: "12px",
          }}
        >
          <Loader2 className="animate-spin" style={{ width: 14, height: 14, color: "#3b82f6" }} />
          <span>Screening <strong style={{ color: "#f1f5f9" }}>&ldquo;{screeningFor}&rdquo;</strong>…</span>
        </div>
      )}

      {/* Result */}
      {result && result.found === false && <NotFoundCard result={result} />}
      {result && result.found !== false && (
        <>
        <div style={{ marginTop: "20px" }}>
          <IdentityResultCard person={fromSanctions(result, risk, queryName)} />
        </div>
        <div
          style={{ background: "#0d1117", border: `1px solid ${result.is_sanctioned ? "#dc2626" : (result.total_matches > 0 ? "#ef4444" : "#22c55e")}`, borderRadius: "12px", padding: "24px", marginTop: "20px" }}
          data-testid="opensanctions-result-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Globe style={{ color: result.is_sanctioned ? "#dc2626" : (result.total_matches > 0 ? "#ef4444" : "#22c55e"), width: 22, height: 22 }} />
              <h3 style={{ color: "#f1f5f9", fontSize: "18px", fontWeight: 700, margin: 0 }}>{result.query_name}</h3>
              {result.is_sanctioned && (
                <span style={{ background: "rgba(220, 38, 38, 0.20)", color: "#fca5a5", border: "1px solid #dc2626", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", fontWeight: 700, letterSpacing: "0.5px" }}>
                  🚨 SANCTIONED
                </span>
              )}
              <span style={{ background: result.total_matches > 0 ? "rgba(239,68,68,0.15)" : "rgba(34,197,94,0.15)", color: result.total_matches > 0 ? "#ef4444" : "#22c55e", borderRadius: "6px", padding: "4px 10px", fontSize: "11px", fontWeight: 700 }}>
                {result.total_matches} MATCH{result.total_matches === 1 ? "" : "ES"}
              </span>
              {typeof result.elapsed_ms === "number" && (
                <span
                  data-testid="screening-elapsed-ms"
                  style={{ background: "rgba(59, 130, 246, 0.12)", color: "#93c5fd",
                           border: "1px solid #2563eb55", borderRadius: "6px",
                           padding: "4px 10px", fontSize: "11px", fontWeight: 700,
                           fontFamily: "ui-monospace, monospace" }}
                  title="End-to-end screening latency"
                >
                  {result.elapsed_ms} ms
                </span>
              )}
            </div>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-opensanctions-btn"
              style={{ background: "linear-gradient(135deg, #2563eb, #1d4ed8)", border: "none", borderRadius: "6px", color: "white", padding: "8px 16px", fontWeight: 600, fontSize: "12px", cursor: saving ? "wait" : "pointer", display: "flex", alignItems: "center", gap: "6px" }}
            >
              {saving ? <Loader2 className="animate-spin" style={{ width: 12, height: 12 }} /> : <FileText style={{ width: 12, height: 12 }} />}
              {saving ? "Saving..." : "Save & Flag"}
            </button>
          </div>

          {/* India-specific flags */}
          <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
            🇮🇳 India-Specific Watchlists
          </div>
          <IndiaFlagsRow flags={result.india_specific_flags} />

          {/* Risk flags */}
          {risk?.flags?.length > 0 && (
            <div style={{ marginBottom: "16px" }}>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
                Risk Flags ({risk.flags.length}) · +{risk.risk_score_added} risk points
              </div>
              {risk.flags.map((f, idx) => {
                const c = FLAG_COLORS[f.severity] || FLAG_COLORS.medium;
                return (
                  <div key={idx} style={{ background: c.bg, border: `1px solid ${c.border}`, borderRadius: "6px", padding: "8px 12px", marginBottom: "6px", display: "flex", alignItems: "center", gap: "8px" }}>
                    <AlertTriangle style={{ width: 14, height: 14, color: c.text }} />
                    <span style={{ color: c.text, fontSize: "12px", fontWeight: 600 }}>{f.label}</span>
                    <span style={{ color: c.text, fontSize: "9px", marginLeft: "auto", textTransform: "uppercase", letterSpacing: "1px" }}>{f.severity}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* Matches */}
          {result.matches?.length > 0 && (
            <div>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
                Matched Records ({result.matches.length})
              </div>
              {result.matches.map((m, idx) => <MatchCard key={idx} m={m} />)}
            </div>
          )}

          {/* PEP matches */}
          {result.pep_matches?.length > 0 && (
            <div style={{ marginTop: "12px" }}>
              <div style={{ color: "#64748b", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "1px", marginBottom: "8px" }}>
                PEP Matches ({result.pep_matches.length})
              </div>
              {result.pep_matches.map((p, idx) => (
                <div key={idx} style={{ background: "rgba(245, 158, 11, 0.10)", border: "1px solid #f59e0b", borderRadius: "6px", padding: "8px 12px", marginBottom: "6px" }}>
                  <div style={{ color: "#f59e0b", fontSize: "12px", fontWeight: 600 }}>{p.name || p}</div>
                </div>
              ))}
            </div>
          )}

          {result.total_matches === 0 && (
            <div style={{ color: "#22c55e", fontSize: "13px", textAlign: "center", padding: "16px" }}>
              ✓ CLEAR — no sanctions matches across OFAC, UN, EU, SEBI, NSE, RBI, MHA UAPA.
            </div>
          )}

          <div style={{ marginTop: "12px", color: "#475569", fontSize: "10px", textAlign: "right" }}>
            AI: {result.ai_mode || "claude-sonnet-4.5"} · Source: {result.source}
          </div>
        </div>
        </>
      )}

      {/* History */}
      {!compact && (
        <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: "12px", padding: "24px", marginTop: "24px" }}>
          <h2 style={{ fontSize: "16px", fontWeight: 700, margin: "0 0 16px", color: "#f1f5f9" }}>Recent Screenings ({history.length})</h2>
          {history.length === 0 ? (
            <div style={{ color: "#64748b", fontSize: "13px", textAlign: "center", padding: "32px" }}>
              No screenings yet. Try "Mehul Choksi" ya "Nirav Modi" se sanctions hit dekhne ke liye.
            </div>
          ) : (
            <div style={{ display: "grid", gap: "8px" }}>
              {history.map((l) => {
                const r = l.result || {};
                return (
                  <div key={l.id} style={{ background: "#080c12", border: "1px solid #1e2530", borderRadius: "8px", padding: "12px 16px", display: "grid", gridTemplateColumns: "1fr auto auto auto", alignItems: "center", gap: "16px" }}>
                    <div style={{ color: "#f1f5f9", fontSize: "13px", fontWeight: 600 }}>{l.query_name}</div>
                    {r.is_sanctioned && <span style={{ background: "rgba(220, 38, 38, 0.18)", color: "#fca5a5", borderRadius: "4px", padding: "2px 8px", fontSize: "10px", fontWeight: 700 }}>SANCTIONED</span>}
                    <div style={{ color: r.total_matches > 0 ? "#ef4444" : "#22c55e", fontSize: "11px" }}>{r.total_matches || 0} match(es)</div>
                    <div style={{ color: "#64748b", fontSize: "10px" }}>{new Date(l.created_at).toLocaleDateString()}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function OpenSanctionsPage() {
  return (
    <div data-testid="opensanctions-page" style={{ color: "#f1f5f9" }}>
      <div className="flex items-center gap-3 mb-2">
        <Globe style={{ width: 26, height: 26, color: "#2563eb" }} />
        <h1 style={{ fontSize: "26px", fontWeight: 700, margin: 0, color: "#f1f5f9" }} data-testid="page-title">OpenSanctions Screening</h1>
      </div>
      <p style={{ color: "#94a3b8", fontSize: "13px", marginBottom: "24px" }}>
        Screen against OFAC · UN · EU · SEBI · NSE · RBI · MHA UAPA — 200+ global &amp; India-specific watchlists.
      </p>
      <OpenSanctionsPanel />
    </div>
  );
}
